import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacyFormFieldAppearance as MatFormFieldAppearance } from '@angular/material/legacy-form-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AclPermissionDetail } from '@y42/acl/models';
import { createAclPermissionDetail } from '@y42/acl/util/create-acl-permission-detail';
import { InputObservable, connectState } from '@y42/angular/util';
import { findUniqueValuesByKey } from '@y42/util/find-unique-values';
import { showLoaderFor } from '@y42/util/show-loader-for-x-ms';
import {
  CellClickedEvent,
  ColDef,
  ColumnApi,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowClickedEvent,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { Observable, filter, map, of, take, tap } from 'rxjs';
import { AbstractListViewService } from './abstract-list-view.service';
import { ListViewDataBase, ListViewGridMessages } from './list-view.model';
import { QuickFilterModel } from './model/quick-filter.model';

@UntilDestroy()
@Component({
  selector: 'y42-list-view',
  templateUrl: './list-view.component.html',
  styleUrls: ['./list-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListViewComponent<D extends ListViewDataBase = ListViewDataBase> implements OnInit {
  constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly service: AbstractListViewService<D>,
    private readonly breakpointObserver: BreakpointObserver,
  ) {
    this.breakpointObserver
      .observe(['(min-width: 1px) and (max-width: 776px)'])
      .pipe(
        tap((result) => (this.sideNavOpened = !result.matches)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  @Input() id = '';
  @Input() name = '';

  @Input()
  @InputObservable()
  addPermissionAllowed = createAclPermissionDetail({ allowed: false });
  private readonly addPermissionAllowed$!: Observable<AclPermissionDetail>;

  @Input() canAdd = true;
  @Input() canEdit = true;
  @Input() hasPadding = true;
  @Input() addButtonText = 'Add...';

  /**
   * Provide a template to view the currently selected row in a split view.
   *
   * Access the selected item by writing:
   * ```
   * <ng-template let-item>
   * ```
   */
  @Input() detailViewTmpl: TemplateRef<unknown> | null = null;

  /**
   * Don't hide the search field even when there is no data available.
   * Useful when defining additional (e.g. date) filter fields, like in the datasource-table-job list-view.
   */
  @Input() hideSearchWhenNoData = true;

  /**
   * Defines the position of the search field.
   * `outside` will occupy the whole width of the page. `inside` will occupy the whole width of sidenav content.
   */
  @Input() searchFieldPosition: 'outside' | 'inside' = 'outside';

  // search-field options
  @Input() hideSearch = false;
  @Input() searchLabel = '';
  @Input() searchPlaceholder = '';
  @Input() searchFieldClasses = 'generic-search-button no-padding';
  @Input() searchFieldAppearance: MatFormFieldAppearance = 'outline';

  // quick-filter options
  @Input() hideQuickFilters = false;
  @Input() quickFilterKey?: string;
  @Input() defaultQuickFilters: QuickFilterModel[] = [];

  // grid options
  @Input() messages?: ListViewGridMessages;
  @Input() columnDefs: ColDef[] = [];
  @Input() gridOptions: GridOptions = {};
  @Input() frameworkComponents: GridOptions['components'] = {};
  @Input() noRowsMessage: { text?: string; body?: string } = {};
  @Input() hideLoader = false;
  @Input() incrementalLoading = false;

  // events
  @Output() add = new EventEmitter<void>();
  @Output() gridReady = new EventEmitter<GridOptions>();
  @Output() gridRowClicked = new EventEmitter<RowClickedEvent>();
  @Output() gridRowDoubleClicked = new EventEmitter<RowDoubleClickedEvent>();
  @Output() gridSelectionChanged = new EventEmitter<SelectionChangedEvent>();
  @Output() activeItemChange = new EventEmitter<D | null>();
  @Output() filterSet = new EventEmitter<void>();
  @Output() viewReset = new EventEmitter<void>();
  @Output() cellClicked = new EventEmitter<CellClickedEvent>();

  @ViewChild('detailContainer', { static: false }) detailContainer?: ElementRef;

  @Input() searchCtrl = new FormControl<string>('', { nonNullable: true });

  sideNavOpened = true;
  private readonly screenSizeChangedTimerMs = 400;

  private readonly numberOfEntries$ = this.service.items$.pipe(map((data) => findUniqueValuesByKey(data, 'id').length));
  readonly state = connectState(this, {
    data: this.service.items$,
    numberOfEntries: this.numberOfEntries$,
    hasData: this.numberOfEntries$.pipe(map((numberOfEntries) => numberOfEntries > 0)),
    activeItem: this.service.activeItem$,
    isSmallScreen: of(false),
    isLoading: this.service.loading$.pipe(
      // keep the loader visible for at least half a second for better UX
      showLoaderFor(250),
    ),
    initialized: this.service.loading$.pipe(
      filter((loading) => !loading),
      // give the quick-filters time to initialize
      // otherwise the whole page jumps a bit
      map(() => true),
      take(1),
    ),
    addPermissionAllowed: this.addPermissionAllowed$,
  });

  gridApi!: GridApi;
  columnApi!: ColumnApi;

  ngOnInit() {
    this.service.getAll?.();
  }

  onGridReady({ api, columnApi }: GridReadyEvent) {
    this.gridApi = api!;
    this.columnApi = columnApi!;
    this.gridReady.emit({ api, columnApi });
  }

  rowClicked(evt: RowClickedEvent) {
    const item: D | null = evt.data;
    this.service.setActiveItem(item);
    this.gridRowClicked.emit(evt);
  }

  rowDoubleClicked(evt: RowClickedEvent) {
    const item: D | null = evt.data;
    this.service.setActiveItem(item);
    this.gridRowDoubleClicked.emit(evt);
  }

  addItem() {
    this.service.add?.();
    this.add.emit();
  }

  resetView() {
    this.gridApi.setFilterModel(null);
    this.columnApi.resetColumnState();

    if (this.searchCtrl.value) {
      this.searchCtrl.setValue('');
    }

    if (this.state.isSmallScreen) {
      this.sideNavOpened = false;
    }
    this.viewReset.emit();
  }

  toggleSidebar() {
    this.sideNavOpened = !this.sideNavOpened;
    setTimeout(() => {
      this.gridApi.sizeColumnsToFit();
    }, this.screenSizeChangedTimerMs);
  }

  filterWasSet() {
    if (this.state.isSmallScreen) {
      this.sideNavOpened = false;
    }
    this.filterSet.emit();
  }

  closeDetailView() {
    this.service.setActiveItem(null);
    this.cdRef.markForCheck();
  }
}
